import { Location } from '@angular/common';
import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import {
  CacheMechanism,
  LocalizeParser,
  LocalizeRouterModule,
  LocalizeRouterSettings,
} from '@gilsdav/ngx-translate-router';
import { COOKIE_DOMAIN, COOKIE_LANG } from './app.constants';
import { AccountsVerifiedGuard } from './accounts/guards/verified.guard';
import { ManualLoaderFactory } from './app.utils';
import { CustomerExistsGuard } from './customers';
import { FeatureFlagGuard } from './shared/guards/feature.guard';
import { CustomTranslateService } from './core/services/custom-translate.service';
import { Auth0Guard } from '@geneplanet/ngx-auth0';
import { CustomPreloadingStrategy } from './core/utils/custom-preload-strategy';
import { AddDataMenuComponent, ResultsMenuComponent } from './core/pages';
import { MobileGuard } from './core/guards/mobile.guard';
import { canActivateFeature } from './shared/guards/feature-active.guard';

const appRoutes: Routes = [
  {
    path: 'me',
    data: { skipRouteLocalization: true },
    loadChildren: () => import('./me/me.module').then((m) => m.MeModule),
  },
  {
    path: 'healthscore',
    data: { skipRouteLocalization: true },
    loadChildren: () =>
      import('./healthscore/healthscore.module').then(
        (m) => m.HealthscoreModule
      ),
  },
  {
    path: 'prs',
    data: { skipRouteLocalization: true },
    loadChildren: () => import('./prs/prs.module').then((m) => m.PrsModule),
  },
  {
    path: 'accounts',
    data: { skipRouteLocalization: true },
    loadChildren: () =>
      import('./accounts/accounts.module').then((m) => m.AccountsModule),
  },
  {
    canActivate: [FeatureFlagGuard],
    path: 'microbiome',
    loadChildren: () =>
      import('./oral-microbiome/oral-microbiome.module').then(
        (m) => m.OralMicrobiomeModule
      ),
    data: { feature: 'oralMicrobiome' },
  },
  {
    path: 'dashboard',
    data: { skipRouteLocalization: true },
    loadChildren: () =>
      import('./dashboard/dashboard.module').then((m) => m.DashboardModule),
  },

  {
    path: 'foodid',
    data: { skipRouteLocalization: true },
    loadChildren: () =>
      import('./foodid/foodid.module').then((m) => m.FoodIdModule),
  },
  {
    path: 'nipt',
    data: { feature: 'nipt', skipRouteLocalization: true },
    loadChildren: () => import('./nipt/nipt.module').then((m) => m.NiptModule),
    canActivateChild: [FeatureFlagGuard],
  },
  {
    path: 'onboarding',
    data: { skipRouteLocalization: true },
    loadChildren: () =>
      import('./onboarding/onboarding.module').then((m) => m.OnboardingModule),
  },
  {
    path: 'ancestry',
    canActivateChild: [
      Auth0Guard,
      AccountsVerifiedGuard,
      CustomerExistsGuard,
      FeatureFlagGuard,
    ],
    data: { feature: 'ancestry', skipRouteLocalization: true, noPreload: true },
    loadChildren: () =>
      import('./ancestry/ancestry.module').then((m) => m.AncestryModule),
  },
  {
    path: '',
    data: { skipRouteLocalization: { localizeRedirectTo: true } },
    redirectTo: '/dashboard',
    pathMatch: 'full',
  },
  {
    data: { skipRouteLocalization: true },
    path: '',
    canActivateChild: [
      AccountsVerifiedGuard,
      CustomerExistsGuard,
      canActivateFeature('shop', '/error/shop-unavailable'),
    ],
    loadChildren: () =>
      import('@geneplanet/ngx-shop/src/lib/shop').then((m) => m.ShopModule),
  },
  {
    path: 'habits',
    data: { skipRouteLocalization: true },
    canActivateChild: [Auth0Guard, AccountsVerifiedGuard, CustomerExistsGuard],
    loadChildren: () =>
      import('./habits/habits.module').then((m) => m.HabitsModule),
  },
  {
    path: 'blood',
    canActivateChild: [Auth0Guard, AccountsVerifiedGuard, CustomerExistsGuard],
    data: { skipRouteLocalization: true },
    loadChildren: () =>
      import('./blood/blood.module').then((m) => m.BloodModule),
  },
  {
    path: 'measurement',
    data: { skipRouteLocalization: true },
    canActivateChild: [CustomerExistsGuard],
    loadChildren: () =>
      import('./body-measurements/body-measurements.module').then(
        (m) => m.BodyMeasurementsModule
      ),
  },
  {
    path: 'dna',
    data: { skipRouteLocalization: true },
    canActivateChild: [Auth0Guard, CustomerExistsGuard],
    loadChildren: () => import('./dna/dna.module').then((m) => m.DnaModule),
  },
  {
    path: 'dna-snp',
    data: { skipRouteLocalization: true },
    canActivateChild: [Auth0Guard, CustomerExistsGuard],
    loadChildren: () =>
      import('./dna-snp/dna-snp.module').then((m) => m.DnaSNPModule),
  },
  {
    path: 'recipes',
    data: { skipRouteLocalization: true },
    canActivateChild: [Auth0Guard, AccountsVerifiedGuard, CustomerExistsGuard],
    loadChildren: async () => (await import('./recipes/routes')).routes,
  },
  {
    path: 'error',
    data: { skipRouteLocalization: true },
    loadChildren: () =>
      import('./error/error.module').then((m) => m.ErrorModule),
  },
  {
    path: 'results',
    data: {
      skipRouteLocalization: true,
      layout: {
        sideMenu: {
          hide: true,
        },
      },
    },
    canActivate: [MobileGuard, Auth0Guard],
    component: ResultsMenuComponent,
  },
  {
    path: 'add-data',
    data: {
      skipRouteLocalization: true,
      layout: {
        sideMenu: {
          hide: true,
        },
      },
    },
    canActivate: [MobileGuard, Auth0Guard],
    component: AddDataMenuComponent,
  },
  {
    path: '**',
    redirectTo: '/dashboard',
    data: { skipRouteLocalization: true },
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(appRoutes, {
      onSameUrlNavigation: 'reload',
      preloadingStrategy: CustomPreloadingStrategy,
      paramsInheritanceStrategy: 'always',
      scrollPositionRestoration: 'enabled',
      bindToComponentInputs: true,
    }),
    LocalizeRouterModule.forRoot(appRoutes, {
      parser: {
        provide: LocalizeParser,
        useFactory: ManualLoaderFactory,
        deps: [CustomTranslateService, Location, LocalizeRouterSettings],
      },
      defaultLangFunction: (languages, cachedLang, browserLang) => {
        let locationCode = cachedLang || browserLang;
        if (!languages.includes(locationCode)) {
          locationCode = languages[0];
        }

        return locationCode;
      },
      cacheName: COOKIE_LANG,
      cacheMechanism: CacheMechanism.Cookie,
      cookieFormat: `{{value}};{{expires}};path=/;domain=${COOKIE_DOMAIN}`,
      useCachedLang: true,
      alwaysSetPrefix: false,
    }),
  ],
  exports: [RouterModule, LocalizeRouterModule],
})
export class AppRoutingModule {}
